/**
 * NeedsBanner — the page-level surface of the NEEDS system (founder 2026-07-13:
 * "a big warning at the top"). At the top of each dashboard page it lists the
 * setup needs that still affect THAT page's cards — "We're missing your overhead
 * — fix it → · affects 3 cards on this page" — one line per unmet need, in
 * registry order. It reads the SAME server-derived needs the cards read and the
 * setup checklist reads, so all three always agree.
 *
 * Dismissible per session: the founder wants it VISIBLE, so dismissing collapses
 * it to a small pill (never gone) that clicks back open. Never shown on the
 * public demo, the setup pages (the checklist is doing that job there) or the
 * agency/operator pages (that's a client's setup, not the operator's).
 *
 * Mounted once in AppLayout; it self-scopes by URL segment and self-hides when a
 * page has no affected cards — so pages with nothing to nag about (Products,
 * Retention) show nothing.
 */

import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  NEEDS,
  PAGE_CARD_NEEDS,
  bannerNeedLines,
  unmetNeeds,
  type NeedStatus,
} from "@data-dashboard/shared";
import { useApiClient } from "./api-context.js";
import { useDemoMode } from "./demo-mode.js";
import type { Capabilities } from "./card-requirements.js";

/** The needs list for the current account (from the shared capabilities fetch —
 *  same query key, so it dedupes with the cards' caps). `enabled` keeps it from
 *  firing on pages that never show the banner. */
function useNeeds(enabled: boolean): NeedStatus[] | undefined {
  const api = useApiClient();
  const q = useQuery({
    queryKey: ["capabilities"],
    enabled,
    queryFn: () => api.get<Capabilities & { locked?: boolean }>("/dashboard/capabilities"),
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
  return q.data?.needs;
}

const AMBER = "#d97706";

export function NeedsBanner(): React.ReactElement | null {
  const location = useLocation();
  const demo = useDemoMode();
  const seg = location.pathname.split("/")[1] || "dashboard";

  // Only the dashboard pages that actually have need-dependent cards, and never
  // the demo / setup / agency surfaces.
  const onSetup = location.pathname.startsWith("/welcome") || location.pathname.startsWith("/onboarding");
  const onAgency = location.pathname.startsWith("/agency") || location.pathname.startsWith("/admin");
  const eligible = !demo && !onSetup && !onAgency && seg in PAGE_CARD_NEEDS;

  const collapseKey = `ccd.needsBanner.collapsed.${seg}`;
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return sessionStorage.getItem(collapseKey) === "1"; } catch { return false; }
  });

  const needs = useNeeds(eligible);

  if (!eligible || !needs) return null;
  const unmetIds = unmetNeeds(needs).map((n) => n.id);
  const lines = bannerNeedLines(seg, unmetIds);
  if (lines.length === 0) return null;

  const collapse = (): void => {
    try { sessionStorage.setItem(collapseKey, "1"); } catch { /* private mode — collapse for this view only */ }
    setCollapsed(true);
  };
  const expand = (): void => {
    try { sessionStorage.removeItem(collapseKey); } catch { /* ignore */ }
    setCollapsed(false);
  };

  // Collapsed → a small, still-visible pill (founder: "not gone").
  if (collapsed) {
    const n = lines.length;
    return (
      <button onClick={expand} style={styles.pill} aria-label="Show setup needs">
        <span aria-hidden="true">⚠️</span>
        {n === 1 ? "1 setup need" : `${n} setup needs`} still affect this page
        <span style={{ color: AMBER, fontWeight: 700 }}>show</span>
      </button>
    );
  }

  return (
    <div style={styles.wrap} role="status">
      <button aria-label="Collapse" onClick={collapse} style={styles.close}>✕</button>
      <div style={styles.title}>
        <span aria-hidden="true">⚠️</span> Finish your setup to unlock the full picture
      </div>
      <div style={styles.lines}>
        {lines.map(({ needId, count }) => {
          const def = NEEDS[needId];
          return (
            <div key={needId} style={styles.line}>
              <span style={styles.lead}>{def.bannerLead}</span>{" "}
              <Link to={def.fixTo} style={styles.fix}>fix it →</Link>
              <span style={styles.count}> · affects {count} {count === 1 ? "card" : "cards"} on this page</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  wrap: {
    position: "relative",
    background: `color-mix(in srgb, ${AMBER} 8%, var(--surface))`,
    border: `1.5px solid color-mix(in srgb, ${AMBER} 45%, var(--border))`,
    borderRadius: "var(--r-sm)",
    padding: "14px 16px",
    marginBottom: 20,
  },
  title: { fontSize: 14, fontWeight: 800, color: AMBER, marginBottom: 8, letterSpacing: "-0.01em", display: "flex", alignItems: "center", gap: 6 },
  lines: { display: "flex", flexDirection: "column", gap: 6 },
  line: { fontSize: 13, color: "var(--text)", lineHeight: 1.5 },
  lead: { fontWeight: 600 },
  fix: { color: AMBER, fontWeight: 700, textDecoration: "none", whiteSpace: "nowrap" },
  count: { color: "var(--text-faint)", fontSize: 12 },
  close: {
    position: "absolute", top: 9, right: 11,
    background: "transparent", border: "none", cursor: "pointer",
    color: "var(--text-faint)", fontSize: 14, lineHeight: 1, padding: 4,
  },
  pill: {
    display: "inline-flex", alignItems: "center", gap: 7,
    background: `color-mix(in srgb, ${AMBER} 8%, var(--surface))`,
    border: `1px solid color-mix(in srgb, ${AMBER} 40%, var(--border))`,
    borderRadius: 999,
    padding: "5px 12px",
    marginBottom: 16,
    color: "var(--body)",
    fontSize: 12,
    fontWeight: 600,
    cursor: "pointer",
  },
};
